import React, { useMemo } from 'react';
import { X, History, Clock, Target, Trophy, User } from 'lucide-react';
import { MatchState } from '../../types';
import { formatDuration } from '../../utils';
import { getAllMatchPoints } from '../../utils/matchHistoryUtils';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface MatchHistoryModalProps {
  isOpen: boolean;
  matchState: MatchState;
  onClose: () => void;
}

export function MatchHistoryModal({ isOpen, matchState, onClose }: MatchHistoryModalProps) {
  const { t } = useTranslation();

  // ✅ HOOKS D'ABORD - toujours appelés dans le même ordre
  const completeMatchData = useMemo(() => {
    const players = {
      A: matchState.matchInfo.playerA,
      B: matchState.matchInfo.playerB
    };

    // ✅ UTILISER LA MÊME FONCTION QUE useResultSender
    const allMatchPoints = getAllMatchPoints(matchState);
    
    // ✅ AJOUTER LES LIGNES DE FIN DE JEU
    const allPointsWithGameEnds: any[] = [];
    
    // Regrouper les points par jeu
    const pointsByGame: { [gameNumber: number]: any[] } = {};
    allMatchPoints.forEach(point => {
      if (!pointsByGame[point.gameNumber]) {
        pointsByGame[point.gameNumber] = [];
      }
      pointsByGame[point.gameNumber].push(point);
    });
    
    // Assembler points + lignes de fin de jeu
    Object.keys(pointsByGame)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .forEach(gameNumber => {
        const gameNum = parseInt(gameNumber);
        
        // Ajouter tous les points du jeu
        pointsByGame[gameNum].forEach(point => {
          allPointsWithGameEnds.push(point);
        });
        
        // Ajouter la ligne de fin de jeu si le jeu est terminé
        const gameResult = matchState.gameResults[gameNum - 1];
        if (gameResult) {
          // Utiliser setScoresAtEnd si disponible, sinon fallback sur le calcul
          const setsA = gameResult.setScoresAtEnd?.A ?? (gameResult.winner === 'A' ? gameNum : gameNum - 1);
          const setsB = gameResult.setScoresAtEnd?.B ?? (gameResult.winner === 'B' ? gameNum : gameNum - 1);

          allPointsWithGameEnds.push({
            isGameEnd: true,
            gameNumber: gameNum,
            winner: gameResult.winner,
            winnerName: gameResult.winner === 'A' ? players.A : players.B,
            scoreA: gameResult.scoreA,
            scoreB: gameResult.scoreB,
            duration: gameResult.duration,
            setsA,
            setsB,
            winnerColor: gameResult.winner === 'A' ? matchState.playerAColor : matchState.playerBColor
          });
        }
      });

    return {
      points: allPointsWithGameEnds,
      games: matchState.gameResults.map((game, index) => ({
        gameNumber: index + 1,
        winner: game.winner,
        winnerName: game.winner === 'A' ? players.A : players.B,
        scoreA: game.scoreA,
        scoreB: game.scoreB,
        duration: game.duration,
        wonByConduct: game.wonByConduct || false,
        wonByInjury: game.wonByInjury || false
      })),
      players,
      totalPoints: allMatchPoints.length, // Points sans les lignes de fin
      totalGames: matchState.gameResults.length
    };
  }, [matchState]);

  // Fonction pour identifier le joueur sanctionné
  const getSanctionedPlayer = (decision: string, gameNumber: number, pointScore: {A: number, B: number}) => {
    if (!['CW', 'CS', 'CG'].includes(decision)) return null;
    
    const typeMapping: {[key: string]: string} = {
      'CW': 'conduct_warning',
      'CS': 'conduct_point', 
      'CG': 'conduct_game'
    };
    
    const sanctionType = typeMapping[decision];
    
    // Pour CS/CG, ajuster le score car la sanction est enregistrée avant l'attribution du point
    let searchScoreA = pointScore.A;
    let searchScoreB = pointScore.B;
    
    if (decision === 'CS' || decision === 'CG') {
      if (pointScore.A > 0 && pointScore.B === 0) {
        searchScoreA = pointScore.A - 1;
      } else if (pointScore.B > 0 && pointScore.A === 0) {
        searchScoreB = pointScore.B - 1;
      } else if (pointScore.A > pointScore.B) {
        searchScoreA = pointScore.A - 1;
      } else if (pointScore.B > pointScore.A) {
        searchScoreB = pointScore.B - 1;
      }
    }
    
    const sanction = matchState.conductSanctions.find(s => 
      s.type === sanctionType &&
      s.gameNumber === gameNumber &&
      s.gameScore.A === searchScoreA &&
      s.gameScore.B === searchScoreB
    );
    
    return sanction?.player || null;
  };

  // ✅ RETURN CONDITIONNEL APRÈS LES HOOKS
  if (!isOpen) return null;

  // Rendu de la colonne décision (même que DuringMatch)
  const renderDecision = (decision: string | null, point?: any) => {
    if (!decision) return '';
    
    let bgColor = '';
    let textColor = '';
    
    switch (decision) {
      case 'STROKE':
        bgColor = 'bg-black';
        textColor = 'text-white';
        break;
      case 'LET':
        bgColor = 'bg-yellow-400';
        textColor = 'text-black';
        break;
      case 'NOLET':
        bgColor = 'bg-green-500';
        textColor = 'text-white';
        break;
      case 'CW':
        bgColor = 'bg-yellow-500';
        textColor = 'text-white';
        break;
      case 'CS':
        bgColor = 'bg-orange-500';
        textColor = 'text-white';
        break;
      case 'CG':
        bgColor = 'bg-red-500';
        textColor = 'text-white';
        break;
      // Timer decisions
      case 'EQP':
        bgColor = 'bg-cyan-500';
        textColor = 'text-white';
        break;
      case 'INJ':
        bgColor = 'bg-cyan-600';
        textColor = 'text-white';
        break;
      case 'BLD':
        bgColor = 'bg-cyan-700';
        textColor = 'text-white';
        break;
      case 'SHR':
        bgColor = 'bg-cyan-800';
        textColor = 'text-white';
        break;
      default:
        return decision;
    }

    // Vérifier si c'est un conduct ou timer et identifier le joueur concerné
    const isConduct = ['CW', 'CS', 'CG'].includes(decision);
    const isTimer = ['EQP', 'INJ', 'BLD', 'SHR'].includes(decision);

    // Pour les conducts, utiliser getSanctionedPlayer; pour les timers, utiliser decisionFor directement
    let concernedPlayer: 'A' | 'B' | null = null;
    if (isConduct && point) {
      concernedPlayer = getSanctionedPlayer(decision, point.gameNumber || 1, {A: point.scoreA, B: point.scoreB});
    } else if (isTimer && point?.decisionFor) {
      concernedPlayer = point.decisionFor;
    }

    const playerName = concernedPlayer === 'A'
      ? completeMatchData.players.A
      : completeMatchData.players.B;
    
    return (
      <div className="flex items-center justify-center gap-1.5">
        {/* Point coloré à gauche si joueur A concerné */}
        {concernedPlayer === 'A' && (
          <div
            className="w-2 h-2 rounded-full border border-white/50"
            style={{ backgroundColor: matchState.playerAColor }}
            title={playerName}
          ></div>
        )}

        {/* Badge de décision */}
        <span className={`${bgColor} ${textColor} px-1 py-0.5 rounded text-[10px] font-medium`}>
          {decision}
        </span>

        {/* Point coloré à droite si joueur B concerné */}
        {concernedPlayer === 'B' && (
          <div
            className="w-2 h-2 rounded-full border border-white/50"
            style={{ backgroundColor: matchState.playerBColor }}
            title={playerName}
          ></div>
        )}
      </div>
    );
  };

  // ✅ LOGIQUE DE CALCUL DES COLONNES (adaptée pour les données JSON)
  const calculateColumns = (point: any) => {
    let col1 = '', col2 = '', col3 = '', col4 = '';

    // Pour les conducts, timers et autres décisions spéciales
    const isSpecialDecision = ['CW', 'CS', 'CG', 'LET', 'EQP', 'INJ', 'BLD', 'SHR'].includes(point.decision);
    if (isSpecialDecision) {
      if (point.servingPlayer === 'A') {
        if (point.servingSide === 'left') {
          col1 = point.scoreA.toString();
        } else if (point.servingSide === 'right') {
          col2 = point.scoreA.toString();
        } else {
          col1 = point.scoreA.toString();
        }
      }
      if (point.servingPlayer === 'B') {
        if (point.servingSide === 'left') {
          col3 = point.scoreB.toString();
        } else if (point.servingSide === 'right') {
          col4 = point.scoreB.toString();
        } else {
          col3 = point.scoreB.toString();
        }
      }
      
      // Pour les conducts, afficher aussi le score de l'adversaire
      if (point.decision === 'CS' || point.decision === 'CG') {
        if (point.servingPlayer === 'A') {
          col3 = point.scoreB.toString();
        } else if (point.servingPlayer === 'B') {
          col1 = point.scoreA.toString();
        }
      }
    } else {
      // Points réguliers
      if (point.servingPlayer === 'A') {
        if (point.servingSide === 'left') {
          col1 = point.scoreA.toString();
        } else if (point.servingSide === 'right') {
          col2 = point.scoreA.toString();
        } else {
          col1 = point.scoreA.toString();
        }
      } else if (point.servingPlayer === 'B') {
        if (point.servingSide === 'left') {
          col3 = point.scoreB.toString();
        } else if (point.servingSide === 'right') {
          col4 = point.scoreB.toString();
        } else {
          col3 = point.scoreB.toString();
        }
      }
    }
    
    return { col1, col2, col3, col4 };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white shrink-0">
            <div className="flex items-center gap-2">
                <History size={16} />
                <h2 className="text-lg font-bold text-gray-900">
                {t('screens.history.title')}
                </h2>
            </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-100"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Tableau des points */}
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0">
            <tr>
                <th
                  colSpan={2}
                  className="p-2 text-center w-2/5 border-r border-gray-300 font-semibold text-gray-900 bg-gray-50"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <User size={18} style={{ color: matchState.playerAColor }} fill={matchState.playerAColor} />
                    {completeMatchData.players.A}
                  </span>
                </th>
                <th className="p-2 text-center w-1/5 border-r border-gray-300 font-semibold bg-gray-100">
                  {t('screens.history.decision')}
                </th>
                <th
                  colSpan={2}
                  className="p-2 text-center w-2/5 font-semibold text-gray-900 bg-gray-50"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <User size={18} style={{ color: matchState.playerBColor }} fill={matchState.playerBColor} />
                    {completeMatchData.players.B}
                  </span>
                </th>
            </tr>
            </thead>
            <tbody>
              {completeMatchData.points.map((point, index) => {
                // ✅ AJOUTER : Rendu spécial pour les fins de jeu
                if (point.isGameEnd) {
                    return (
                    <tr key={index} className="bg-gray-50 border-y border-gray-200">
                        <td colSpan={5} className="p-3 text-center text-gray-900 font-bold">
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          <User size={14} style={{ color: point.winnerColor }} fill={point.winnerColor} />
                          <span>{t('screens.history.gameWonBy', {
                                  params: {
                                    number: point.gameNumber,
                                    winner: point.winnerName
                                  }
                                })}</span>
                           • 
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {formatDuration(point.duration)}
                          </span>
                           • 
                          <span className="flex items-center gap-1">
                            <Target size={12} />
                            {point.scoreA}-{point.scoreB}
                          </span>
                           • 
                          <span className="flex items-center gap-1">
                            <Trophy size={12} />
                            {point.setsA}-{point.setsB}
                          </span>
                        </div>
                      </td>
                    </tr>
                    );
                }
                
                const { col1, col2, col3, col4 } = calculateColumns(point);
                
                return (
                  <tr 
                    key={index} 
                    className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                    >
                    <td className="p-1 text-center w-1/5 border-r border-gray-200">{col1}</td>
                    <td className="p-1 text-center w-1/5 border-r border-gray-200">{col2}</td>
                    <td className="p-1 text-center w-1/5 border-r border-gray-200">{renderDecision(point.decision, point)}</td>
                    <td className="p-1 text-center w-1/5 border-r border-gray-200">{col3}</td>
                    <td className="p-1 text-center w-1/5">{col4}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {/* Message si aucun point */}
          {completeMatchData.points.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <div className="text-4xl mb-2">🎾</div>
              <div className="font-medium">{t('screens.history.noPoints')}</div>
              <div className="text-sm mt-1">{t('screens.history.emptyHistory')}</div>
            </div>
          )}

          {matchState.conductSanctions.length > 0 && (
          <div className="border-t border-gray-200 bg-gray-50">
            <div className="p-3">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-gray-800">
                  {t('screens.history.conductSanctions')} ({matchState.conductSanctions.length})
                </h3>
              </div>
              
              <div className="space-y-2">
                {matchState.conductSanctions.map((sanction, index) => {
                  const playerName = sanction.player === 'A' 
                    ? completeMatchData.players.A 
                    : completeMatchData.players.B;
                  const playerColor = sanction.player === 'A' 
                    ? matchState.playerAColor 
                    : matchState.playerBColor;
                    
                  return (
                    <div key={index} className="bg-white p-3 border border-gray-200">
                      {/* Ligne principale avec décision et joueur */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {renderDecision(sanction.type === 'conduct_warning' ? 'CW' : 
                                        sanction.type === 'conduct_point' ? 'CS' : 'CG')}
                          <span className="text-sm font-medium" style={{ color: playerColor }}>
                            {playerName}
                          </span>
                          <span className="text-xs text-gray-500">
                            • {t('screens.history.game')} {sanction.gameNumber}
                            • {sanction.gameScore.A}-{sanction.gameScore.B}
                          </span>
                        </div>
                      </div>
                      
                      {/* Justification */}
                      <div className="text-xs text-gray-600 leading-relaxed bg-gray-50 p-2 border-l-2 border-gray-300">
                        {sanction.justification}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}